import { type FC, type KeyboardEvent, useCallback } from "react";

import { Button, Modal } from "@snapchat/snap-design-system";

import { type RemoteApiSpec } from "../../../gql-generated/types";
import {
    DeleteLensModal,
    LensDeleteHeader,
    LensDeleteText,
    deleteButtonStyle,
} from "../../../lens/components/shared/delete-lens";
import { Flexbox } from "../../../shared/components";
import { useToggleState } from "../../../shared/hooks";

import { DropdownAction } from "./DropdownMenu.styles";
import { CancelCta, DeleteConfirmation, DeleteConfirmationMesage, DeleteCta } from "./messages.generated";

interface DropdownMenu {
    onDelete: (api: RemoteApiSpec) => Promise<void>;
    deleteLoading: boolean;
    api: RemoteApiSpec;
}

export const DropdownDeleteAction: FC<DropdownMenu> = ({ onDelete, api, deleteLoading = false }) => {
    const [modalProps, showModal] = useToggleState();
    const { id, name } = api;

    const onKeyPressModalTrigger = useCallback(
        ({ nativeEvent }: KeyboardEvent<HTMLDivElement>) => {
            if (nativeEvent.key !== "Enter") {
                return;
            }
            showModal();
        },
        [showModal],
    );

    const onClickDelete = useCallback(() => {
        if (deleteLoading) {
            return;
        }

        void onDelete(api).then(() => {
            modalProps.onCancel();
        });
    }, [onDelete, api, deleteLoading, modalProps]);

    return (
        <>
            <DropdownAction onClick={showModal} onKeyPress={onKeyPressModalTrigger}>
                <DeleteCta />
            </DropdownAction>
            <Modal
                {...modalProps}
                padBody={false}
                footer={
                    <Flexbox justifyContent="flex-end">
                        <Button onClick={modalProps.onCancel}>
                            <CancelCta />
                        </Button>
                        <Button
                            isPending={deleteLoading}
                            isDisabled={deleteLoading}
                            type="primary"
                            onClick={onClickDelete}
                            className={deleteButtonStyle}
                        >
                            <DeleteCta />
                        </Button>
                    </Flexbox>
                }
            >
                <DeleteLensModal>
                    <LensDeleteHeader>
                        <DeleteConfirmation />
                    </LensDeleteHeader>
                    <LensDeleteText>
                        <DeleteConfirmationMesage id={id} name={name} />
                    </LensDeleteText>
                </DeleteLensModal>
            </Modal>
        </>
    );
};
